import { Capacitor } from "@capacitor/core";
import { reportError } from "./errorTracking";
import { onGenuineForeground } from "./foregroundGate";

/**
 * Seamless Capgo update apply, native-only.
 *
 * Capgo's default behaviour (`autoUpdate: true`, `directUpdate: false`)
 * is: download a new bundle on launch, but apply it only on the NEXT
 * cold launch. That's where the "close and reopen the app twice"
 * instruction came from — it was the user manually triggering the
 * second cold start. With high iteration rate, that friction is
 * meaningful: many users won't do the dance and just stay behind.
 *
 * This module wires Capgo to apply downloaded bundles when the user
 * backgrounds → foregrounds the app (App `pause` then `resume` — NOT
 * appStateChange isActive, which iOS also fires when a system sheet such
 * as Google sign-in or the StoreKit purchase sheet closes; see
 * lib/foregroundGate.ts). The reload happens at the natural "I just opened
 * the app" moment, not mid-workout, and the user sees the new bundle
 * without doing anything. If they never background, the apply still
 * happens on next cold launch (Capgo's normal path), so nothing
 * regresses.
 *
 * applyPendingCapgoUpdate() is also exported so the VersionUpdateBanner
 * can offer a "tap to update now" path — a single click that does
 * what "close and reopen twice" used to.
 *
 * Web is a no-op (different update mechanism, handled by
 * forceReloadToLatest in lib/appUpdate.ts).
 */

interface PendingUpdate {
  id: string;
  version?: string;
}

let pending: PendingUpdate | null = null;
let initialised = false;
let applyInFlight = false;

// Set when the user explicitly taps "Update" but the target bundle isn't
// downloaded yet (so the tap can't apply immediately and would otherwise
// require a SECOND tap once the download lands — founder report
// 2026-07-27 "only on the second click it actually updates"). While this
// intent is live, the downloadComplete listener auto-applies the moment
// the bundle finishes downloading, so one tap is enough. Cleared after it
// fires or after a 2-minute safety window so a stale intent can't apply an
// update the user has since moved on from.
let applyRequestedAt = 0;
const APPLY_INTENT_MAX_AGE_MS = 2 * 60 * 1000;

/**
 * Called by the update pill the moment the user taps "Update". Records
 * that an apply is wanted so that if the bundle still needs downloading,
 * the downloadComplete listener can finish the job automatically — no
 * second tap.
 */
export function requestApplyOnDownload(): void {
  applyRequestedAt = Date.now();
}

export function hasPendingCapgoUpdate(): boolean {
  return !!pending;
}

/**
 * A live workout means NO update applies (founder incident 2026-07-03:
 * a bundle swap mid-Strong-workout reloaded the WebView and the session
 * "dropped with no trace"). Checked at every apply entry point:
 *   • Strong/Heavy: the active-workout ref persists for the whole session.
 *   • Quick: the simple-mode draft with a running/done-but-unsaved phase.
 * The pending bundle simply waits — it applies at the next foregrounding
 * after the workout ends, or on the next cold start.
 */
const WORKOUT_MARKER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** When this load first saw an unstampable marker (storage full). */
let unstampedSeenAt: number | null = null;

export function isWorkoutInProgress(): boolean {
  try {
    const marker = localStorage.getItem("trybeup_active_workout");
    if (marker) {
      // Markers older than 24h are abandoned sessions, not live workouts —
      // without this cutoff one forgotten workout blocks OTA updates forever.
      // Markers written before the `ts` field existed get stamped now, so
      // they too age out within 24h instead of blocking indefinitely.
      try {
        const parsed = JSON.parse(marker);
        if (typeof parsed?.ts === "number") {
          if (Date.now() - parsed.ts <= WORKOUT_MARKER_MAX_AGE_MS) return true;
        } else if (parsed && typeof parsed === "object") {
          try {
            localStorage.setItem(
              "trybeup_active_workout",
              JSON.stringify({ ...parsed, ts: Date.now() }),
            );
          } catch {
            // Storage full: the marker can't be stamped, so age it from when
            // this load first saw it — otherwise it blocked updates forever
            // (review 2026-10-17).
            if (unstampedSeenAt === null) unstampedSeenAt = Date.now();
            return Date.now() - unstampedSeenAt <= WORKOUT_MARKER_MAX_AGE_MS;
          }
          return true;
        }
      } catch {
        // Unparseable marker: not a workout we can resume, so it must not hold
        // updates back forever (it re-armed the native delay every launch —
        // review 2026-10-17). Drop it — and still check the simple-workout
        // draft below: a running simple workout must hold updates even when
        // the other marker is junk.
        try { localStorage.removeItem("trybeup_active_workout"); } catch { /* ignore */ }
      }
    }
    const raw = localStorage.getItem("trybeup_simple_workout_draft");
    if (raw) {
      const draft = JSON.parse(raw);
      if (draft?.phase === "running" || draft?.phase === "done") {
        // Same 24h cutoff as loadSimpleDraft() in MyWorkouts — a draft the
        // workout tab would discard on sight must not block updates either.
        const startedAt = draft?.workoutStartedAt;
        if (typeof startedAt !== "number") return false;
        return Date.now() - startedAt <= WORKOUT_MARKER_MAX_AGE_MS;
      }
    }
  } catch { /* unreadable storage — err on the side of updating */ }
  return false;
}

// ── Native side of the workout guard (review 2026-10-17) ─────────────
// isWorkoutInProgress() only stops OUR apply paths. Capgo's native side
// installs a downloaded bundle by itself when the app goes to the
// background — the "checking a text between sets" moment — and reloads the
// WebView under the workout. While a workout is live, tell the plugin to
// wait for a kill instead (setMultiDelay kind 'kill'); lift it when the
// workout ends (cancelDelay), which hands the bundle back to the normal
// next-background install.
//
// Synced on start-up, every few seconds while the app is open (a workout
// starts from a tap, and the next background can come seconds later), and
// on pause as a last chance. Native calls happen only when the wanted state
// changes. The flag in localStorage remembers that WE set a delay, so a
// leftover one is lifted after a relaunch even if no workout is running.
const WORKOUT_DELAY_FLAG_KEY = "trybeup_capgo_workout_delay";
const WORKOUT_DELAY_POLL_MS = 3_000;
// undefined = not synced since launch: the native state is unknown (a kill
// clears the plugin's delay), so the first sync always tells the plugin.
let workoutDelaySet: boolean | undefined;
let workoutDelaySync: Promise<void> = Promise.resolve();

type DelayApi = {
  setMultiDelay?: (o: { delayConditions: Array<{ kind: string; value?: string }> }) => Promise<void>;
  cancelDelay?: () => Promise<void>;
};

async function applyWorkoutDelay(): Promise<void> {
  const want = isWorkoutInProgress();
  let flagged = false;
  try { flagged = localStorage.getItem(WORKOUT_DELAY_FLAG_KEY) === "1"; } catch { /* storage blocked */ }
  if (workoutDelaySet === want) return;
  if (workoutDelaySet === undefined && !want && !flagged) {
    // Nothing we set is pending and no workout: leave the plugin alone.
    workoutDelaySet = false;
    return;
  }
  const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
  const updater = CapacitorUpdater as unknown as DelayApi;
  if (want) {
    await updater.setMultiDelay?.({ delayConditions: [{ kind: "kill" }] });
    try { localStorage.setItem(WORKOUT_DELAY_FLAG_KEY, "1"); } catch { /* storage blocked */ }
  } else {
    await updater.cancelDelay?.();
    try { localStorage.removeItem(WORKOUT_DELAY_FLAG_KEY); } catch { /* storage blocked */ }
  }
  workoutDelaySet = want;
}

/** Bring Capgo's native delay in line with "is a workout live". Serialised,
 *  never rejects, no-op on the web. */
export function syncWorkoutUpdateDelay(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve();
  workoutDelaySync = workoutDelaySync
    .then(applyWorkoutDelay)
    .catch((err) => {
      // A plugin without these methods, or a bridge error: try again on the
      // next sync rather than believing a state we never reached.
      workoutDelaySet = undefined;
      // eslint-disable-next-line no-console
      console.warn("[live-update] workout delay sync failed", err);
    });
  return workoutDelaySync;
}

/** Test hook: forget what has been synced since "launch". */
export function __resetWorkoutDelayForTests(): void {
  workoutDelaySet = undefined;
  workoutDelaySync = Promise.resolve();
}

export function getPendingCapgoVersion(): string | undefined {
  return pending?.version;
}

// ── Post-launch confirmation (option D, 2026-06-20) ─────────────────
// When `applyPendingCapgoUpdate()` fails to swap in-process, the bundle
// is still on disk and Capgo's `autoUpdate:true` will pick it up on the
// next cold launch automatically. Rather than show a scary
// "Update couldn't apply automatically" alert at failure time, we
// silently record which version was being staged, then on the next
// launch — once Capgo's native side has applied it — fire a quiet
// "✓ Updated to vX" toast so the user knows it landed. From their POV:
// they tap Update, nothing visible happens, then later (when they
// naturally reopen the app) the new version is just there with a
// brief confirmation.
//
// Keys are stored as { version, savedAt } so we can age out stale
// records — if the user never cold-launches for a week, give up.
const PENDING_APPLY_KEY = "trybeup_capgo_pending_apply";
const PENDING_APPLY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface PendingApplyRecord {
  version: string;
  savedAt: number;
}

/**
 * Synchronous peek: is there a recorded pending-apply that hasn't
 * landed yet? VersionUpdateBanner uses this on mount + after a
 * silent fallback to render the quieter "Update will finish next
 * time you open the app" pill instead of the active "Update" one.
 *
 * Returns the version string if a non-expired pending record exists,
 * null otherwise. Does NOT compare to the running bundle — that's
 * consumePendingCapgoApply()'s job (async; runs once on mount). The
 * sync peek is for render-time gating only.
 */
export function readPendingCapgoApplyVersion(): string | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(PENDING_APPLY_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw) as PendingApplyRecord;
    if (!rec?.version || typeof rec.savedAt !== "number") return null;
    if (Date.now() - rec.savedAt > PENDING_APPLY_MAX_AGE_MS) return null;
    return rec.version;
  } catch {
    return null;
  }
}

/**
 * Mark a bundle version as "tried to apply, set() failed, will land
 * on next cold launch". Called by VersionUpdateBanner on the silent-
 * fallback path. Safe to call when version is unknown (no-op).
 */
export function recordPendingCapgoApply(version: string | undefined): void {
  if (!version) return;
  try {
    const rec: PendingApplyRecord = { version, savedAt: Date.now() };
    localStorage.setItem(PENDING_APPLY_KEY, JSON.stringify(rec));
  } catch { /* quota — non-fatal */ }
}

/**
 * On launch, check whether a pending apply has actually landed (i.e.
 * Capgo's currently-running bundle matches the version we stored).
 * Returns the version that just applied (caller can show a toast) or
 * null. Idempotent: the record is cleared the first time it matches,
 * or when it ages past PENDING_APPLY_MAX_AGE_MS, so the toast only
 * fires once and stale records can't replay forever.
 */
export async function consumePendingCapgoApply(): Promise<string | null> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(PENDING_APPLY_KEY); } catch { return null; }
  if (!raw) return null;
  let rec: PendingApplyRecord | null = null;
  try { rec = JSON.parse(raw) as PendingApplyRecord; } catch { /* fall through */ }
  if (!rec?.version || typeof rec.savedAt !== "number") {
    try { localStorage.removeItem(PENDING_APPLY_KEY); } catch { /* no-op */ }
    return null;
  }
  // Aged out — user never cold-launched within a week. Drop it.
  if (Date.now() - rec.savedAt > PENDING_APPLY_MAX_AGE_MS) {
    try { localStorage.removeItem(PENDING_APPLY_KEY); } catch { /* no-op */ }
    return null;
  }
  if (!Capacitor.isNativePlatform()) {
    // Web has no Capgo bundle concept — clear and don't toast.
    try { localStorage.removeItem(PENDING_APPLY_KEY); } catch { /* no-op */ }
    return null;
  }
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    const current = await CapacitorUpdater.current();
    const runningVersion = current?.bundle?.version;
    if (runningVersion && runningVersion === rec.version) {
      // Landed — clear and signal the caller to toast.
      try { localStorage.removeItem(PENDING_APPLY_KEY); } catch { /* no-op */ }
      return rec.version;
    }
  } catch { /* plugin missing / lookup failed — leave record for next launch */ }
  return null;
}

/**
 * Apply the queued bundle now. Triggers an immediate webview reload
 * via Capgo's `next()` + `reload()` — the user sees ~1s of loading,
 * then the new version. (Not `set()` — see the comment at the apply
 * site for why that primitive caused every update to need two taps.)
 *
 * Returns `true` when the apply was actually fired (caller should
 * expect a webview tear-down momentarily). Returns `false` when
 * nothing happened (nothing pending, already mid-apply, or web build
 * with no native plugin) — caller should fall back to UI that tells
 * the user to cold-restart instead of leaving them on a "loading"
 * spinner forever. Earlier the function silently returned `void` in
 * all the no-op cases, which left the VersionUpdateBanner's "Update"
 * button stuck on "Updating…" when a user tapped it before the
 * downloadComplete event had fired (founder report 2026-06-18 —
 * "I clicked update and it got stuck, had to cold restart").
 *
 * When in-memory `pending` is null (the downloadComplete listener
 * was attached AFTER Capgo finished the auto-download — happens on
 * cold start when the download completed in a prior session) we fall
 * back to `CapacitorUpdater.list()` and pick the newest non-current
 * bundle in the local store. This catches the case where the bundle
 * IS downloaded on disk but our in-memory `pending` flag never got
 * set (founder report 2026-06-19 — "the alert is back, but the bundle
 * banner says 1.1.310 ready").
 */
export async function applyPendingCapgoUpdate(preferredVersion?: string): Promise<boolean> {
  if (applyInFlight || !Capacitor.isNativePlatform()) return false;
  applyInFlight = true;
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");

    // Three-tier lookup for the bundle to apply, in order of cheapest
    // to most-aggressive:
    //   (a) in-memory pending — set by the downloadComplete listener
    //       in THIS session. Fastest, no network.
    //   (b) disk recovery — CapacitorUpdater.list() finds a bundle
    //       that was downloaded in a PRIOR session whose
    //       downloadComplete event our listener missed.
    //   (c) manual download — getLatest() returns the channel's
    //       latest version + URL, then download() fetches the zip
    //       and returns a fresh BundleInfo. Slow (5-60s on cellular)
    //       but the only path that actually PULLS DOWN a bundle Capgo
    //       hasn't downloaded yet. Founder report 2026-06-19: the
    //       Update banner kept saying "ready" but tapping it never
    //       applied because Capgo's auto-download hadn't fired —
    //       this third tier closes that gap.
    const updaterLoose = CapacitorUpdater as unknown as {
      list: () => Promise<{ bundles?: Array<{ id?: string; version?: string; status?: string }> }>;
      current: () => Promise<{ bundle?: { version?: string } }>;
      getLatest: () => Promise<{ version?: string; url?: string; sessionKey?: string; checksum?: string }>;
      download: (opts: { url: string; version: string; sessionKey?: string; checksum?: string }) => Promise<{ id?: string; version?: string }>;
      next: (opts: { id: string }) => Promise<unknown>;
      reload: () => Promise<void>;
    };

    // What the channel actually serves right now. On multi-release days
    // (several bundles shipped in one of the founder's app sessions) the
    // in-memory `pending` can be an EARLIER bundle than the one the banner
    // is advertising — applying it reloads onto an intermediate version,
    // the banner immediately re-appears for the real latest, and the user
    // has to tap Update twice (founder report 2026-07-04: "every time I
    // see an update pill it takes two times"). Knowing the channel-latest
    // version lets tiers (a) and (b) skip stale candidates. Best-effort:
    // if the metadata call fails (offline), fall back to trusting what we
    // have — worst case is the old behavior, never a new failure.
    // Resolve the target version. Try a FRESH tap-time getLatest FIRST — it's
    // more current than the caller's preferredVersion, which is the banner's
    // channel-latest check cached for up to ~60s (it re-runs on an interval,
    // not at tap time). Trusting the cached value alone could lock onto a
    // bundle that's already an intermediate by the time of the tap, forcing a
    // second tap for the real latest (review 2026-07-04, part 2). Fall back to
    // preferredVersion ONLY when the live lookup fails/times out — that's still
    // far better than the original bug where a null latestVersion made tier (a)
    // apply a STALE in-memory `pending` intermediate (the first "two taps").
    let latestVersion: string | null = null;
    try {
      // 5s cap — a hung metadata call would otherwise pin applyInFlight and
      // silently no-op every later banner tap (review 2026-07-04).
      latestVersion = (await Promise.race([
        updaterLoose.getLatest(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 5_000)),
      ]))?.version ?? null;
    } catch { /* metadata unavailable — fall back to the pill's version below */ }
    if (!latestVersion) latestVersion = preferredVersion ?? null;

    // (a) In-memory pending, unless it's visibly stale.
    let targetId: string | null = null;
    if (pending?.id && (!latestVersion || !pending.version || pending.version === latestVersion)) {
      targetId = pending.id;
    }

    // (b) Disk recovery.
    if (!targetId) {
      try {
        const [list, current] = await Promise.all([
          updaterLoose.list(),
          updaterLoose.current(),
        ]);
        const currentVersion = current?.bundle?.version;
        const candidates = (list?.bundles ?? []).filter((b) => {
          if (!b.id || !b.version) return false;
          if (b.version === currentVersion) return false;
          // Skip stale on-disk bundles when we know what the channel's
          // latest is — same two-tap trap as a stale in-memory pending.
          if (latestVersion && b.version !== latestVersion) return false;
          const status = String(b.status ?? "").toLowerCase();
          // Denylist on KNOWN-bad statuses — accept everything else
          // (forward-compatible with future Capgo SDK status strings).
          // "downloading" is half-written on disk: applying it would
          // reload into a broken page, so it's as bad as failed here.
          if (status === "failed" || status === "error" || status === "delete" || status === "downloading") return false;
          return true;
        });
        candidates.sort((a, b) => {
          const av = (a.version ?? "").split(".").map((p) => parseInt(p, 10) || 0);
          const bv = (b.version ?? "").split(".").map((p) => parseInt(p, 10) || 0);
          for (let i = 0; i < Math.max(av.length, bv.length); i++) {
            const diff = (bv[i] ?? 0) - (av[i] ?? 0);
            if (diff !== 0) return diff;
          }
          return 0;
        });
        const winner = candidates[0];
        if (winner?.id) {
          targetId = winner.id;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[live-update] disk-bundle recovery failed", err);
      }
    }

    // (c) Manual download fallback — nothing in memory, nothing on
    // disk. Ask Capgo for the channel's latest, download the zip
    // ourselves, then apply the resulting bundle id. This is the
    // path that fixes the "banner says ready but tap does nothing"
    // chicken-and-egg.
    if (!targetId) {
      try {
        const latest = await updaterLoose.getLatest();
        if (latest?.url && latest?.version) {
          // Is the plugin's OWN autoUpdate download of this exact version
          // already running? Tier (b) correctly refuses a half-written
          // "downloading" bundle, which used to drop us straight into a
          // SECOND download of the same zip. The two raced; the loser was
          // reported as "[capgo] bundle download failed" — the whole
          // recurring alert (confirmed in Capgo's device log 2026-09-06,
          // see lib/capgoBundleWait.ts). Wait for the running one instead:
          // ready → apply it; failed → the manual download below is now the
          // right move; still going after 45s → leave it, downloadComplete
          // auto-applies when it lands and the pill falls back quietly.
          const { findInProgressBundle, waitForBundle } = await import("./capgoBundleWait");
          let inProgress = false;
          try {
            inProgress = !!findInProgressBundle((await updaterLoose.list())?.bundles, latest.version);
          } catch { /* list unavailable — behave as before */ }
          if (inProgress) {
            const waited = await waitForBundle(() => updaterLoose.list(), latest.version);
            if (waited.state === "ready") {
              targetId = waited.id;
            } else if (waited.state === "timeout") {
              applyInFlight = false;
              return false;
            }
            // "failed" falls through to the manual download.
          }
        }
        if (!targetId && latest?.url && latest?.version) {
          const bundle = await updaterLoose.download({
            url: latest.url,
            version: latest.version,
            sessionKey: latest.sessionKey,
            checksum: latest.checksum,
          });
          if (bundle?.id) {
            targetId = bundle.id;
          }
        }
      } catch (err) {
        // Network failure / Capgo plugin error / no new version on
        // the channel. Fall through to the no-op return.
        // eslint-disable-next-line no-console
        console.warn("[live-update] manual download fallback failed", err);
      }
    }

    if (!targetId) {
      // Truly nothing to apply — caller should show the cold-restart
      // fallback alert.
      applyInFlight = false;
      return false;
    }

    // Apply via next() + reload() — NOT set(). This was the root cause of
    // the founder's "every update pill takes two taps" (2026-07-04):
    // set() first marks the new bundle as the ACTIVE one in native storage
    // (status "pending") and then hot-swaps the WKWebView — but the swap is
    // flaky on iOS. When it silently fails, the old page keeps running
    // while natively the un-booted new bundle is "current"; ~10s after the
    // next foreground the plugin's crash-rollback (checkRevert) declares
    // that bundle failed-to-boot, rolls back, force-reloads the webview
    // onto the OLD version, and can even delete the perfectly good bundle.
    // The banner then legitimately re-appears and the user taps again.
    //
    // next() + reload() is the plugin's documented "Restart now button"
    // primitive: reload() stages the queued bundle, swaps, and — critically
    // — on a FAILED swap restores the previous live state natively and
    // KEEPS the bundle staged as `next`, which Capgo then installs by
    // itself at the next background/kill. One tap either applies now or is
    // guaranteed to land on the next app open — never two taps.
    // Re-check the workout guard HERE, not just at the entry points: the
    // tier-c manual download above can take 5-60s on cellular, plenty of
    // time for the user to start a workout after the entry check passed.
    // Reloading now would nuke that live session — the exact 2026-07-03
    // incident class this guard exists for (review 2026-07-04).
    //
    // Do NOT stage it with next() here (this branch used to): a staged
    // bundle is exactly what the plugin installs at the next BACKGROUND —
    // mid-workout (review 2026-10-17). Hold the native delay instead; the
    // bundle stays on disk and applies at the first workout-free foreground
    // or cold start.
    if (isWorkoutInProgress()) {
      void syncWorkoutUpdateDelay();
      applyInFlight = false;
      return false;
    }
    // This tap-initiated apply has committed to a reload, so clear any
    // "apply on next download" intent — otherwise, if the reload teardown is
    // deferred, a later background download (a newer bundle on a multi-
    // release day) could fire the downloadComplete auto-apply and reload the
    // app a SECOND time for a bundle the user never tapped for (review
    // 2026-07-28). The genuine download-wait path leaves the intent set by
    // returning false above.
    applyRequestedAt = 0;
    await updaterLoose.next({ id: targetId });
    await updaterLoose.reload();
    // Normally the reload tears this JS context down within a second, so
    // nothing after it matters. If we're still alive (teardown deferred),
    // release the latch after a grace period so later apply attempts —
    // banner tap, next foregrounding — aren't silently no-oped forever.
    setTimeout(() => { applyInFlight = false; }, 15_000);
    // Don't clear `pending` — the reload below makes the module reset.
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[live-update] apply-on-foreground failed", err);
    applyInFlight = false;
    throw err;
  }
}

/**
 * Wire the Capgo events + App lifecycle listeners. Idempotent — safe
 * to call from main.tsx once at startup; subsequent calls are no-ops.
 */
export function initSeamlessCapgoUpdates(): void {
  if (initialised || !Capacitor.isNativePlatform()) return;
  initialised = true;

  (async () => {
    try {
      const { CapacitorUpdater } = await import("@capgo/capacitor-updater");

      // Capgo fires 'downloadComplete' when a new bundle finishes
      // downloading and is ready to apply. We capture the bundle info
      // and wait for the next foreground to apply it.
      // The Capgo typings vary across versions; cast loosely. The actual
      // payload is { bundle: { id, version, ... } } in recent versions.
      type DownloadCompletePayload = { bundle?: { id?: string; version?: string }; version?: string };
      const updater = CapacitorUpdater as unknown as {
        addListener: (
          event: string,
          listener: (p: DownloadCompletePayload) => void,
        ) => Promise<{ remove: () => Promise<void> }>;
      };

      // Rollback / failure visibility (gap sweep 2026-08-07). Capgo fires
      // `updateFailed` when a bundle fails to become active and it reverts to
      // the last known-good one, and `downloadFailed` when a bundle can't be
      // fetched. Both are silent by default — a bad release could keep everyone
      // on old code with nobody told. Report them through the normal client-error
      // pipeline so they land in the Admin → Errors tab AND fire the crash-alert
      // email, tagged "[capgo]" so the admin update-health view can find them.
      // Best-effort: a plugin without these events just never calls back.
      // Both handlers keep only the version and throw the rest of Capgo's
      // payload away. Three download-failure alerts (1.3.12, 1.3.16, 1.3.18)
      // have now told us THAT a download failed and nothing about why — no
      // status code, no byte count, no reason — which is why the first two
      // hypotheses (bundle size, then the CLI) were guesses. Whatever the
      // plugin hands us goes into the report from here.
      //
      // RESOLVED 2026-09-07: the device id added below let us read the phone's
      // own log in Capgo. Cause: a tap on the Update pill during the plugin's
      // auto download started a second download of the same bundle (tier (c)
      // in applyPendingCapgoUpdate); the loser of the race fails to unzip and
      // fires this event while the winner installs. Fixed at the source — see
      // lib/capgoBundleWait.ts. If this alert reappears, it is a NEW cause.
      //
      // Deliberately attached to `stack`, NOT to the message. The backend
      // fingerprints on `source + message` only (services/error_capture.py),
      // with digits normalised to '#', so the message must stay constant or
      // every device would mint a new fingerprint and the 6-hour throttle
      // would stop collapsing these into one email. Same alert cadence, more
      // to read when you open it.
      const describePayload = (p: unknown): string => {
        try {
          const s = JSON.stringify(p);
          if (!s || s === "{}") return "(plugin sent an empty payload)";
          return s.length > 1000 ? `${s.slice(0, 1000)}… (truncated)` : s;
        } catch {
          return "(payload could not be serialised)";
        }
      };
      // The fourth download-failure alert (1.3.25, 2026-09-06) showed the
      // plugin's payload is ONLY {"version"} — no reason, ever. The reason
      // lives in Capgo's own per-device log, so the report now carries what
      // is needed to find that row: the Capgo device id, the bundle the
      // phone is running, and whether it believed it was online. Resolved
      // in the background at registration; never awaited.
      // `lastStatus` (2026-09-10): the plugin's own words from its last
      // `appReady` — e.g. "Latest version is in error state. Aborting update."
      // The `downloadFailed` event is ALSO fired for those non-download aborts
      // (endBackGroundTaskWithNotif), which is why a phone can report a
      // "download failure" for the very version it is running.
      const context: { deviceId: string; running: string; online?: boolean; lastStatus?: string } = { deviceId: "unknown", running: "unknown" };
      try {
        const loose = CapacitorUpdater as unknown as {
          getDeviceId?: () => Promise<{ deviceId?: string }>;
          current?: () => Promise<{ bundle?: { version?: string } }>;
        };
        // NOT awaited (review of PR #2260): the listeners below are what keep
        // live updates working, and a native bridge call that never settles
        // must not stop them from registering. `context` is only read inside
        // withPayload, long after this resolves.
        Promise.all([
          loose.getDeviceId?.().catch(() => undefined),
          loose.current?.().catch(() => undefined),
        ]).then(([dev, cur]) => {
          if (dev?.deviceId) context.deviceId = dev.deviceId;
          if (cur?.bundle?.version) context.running = cur.bundle.version;
        }).catch(() => { /* diagnostics only */ });
      } catch { /* diagnostics only — never block the listeners */ }
      const withPayload = (err: Error, payload: unknown): Error => {
        // Append rather than replace: the JS stack is boilerplate inside an
        // event callback, but discarding it outright is the kind of thing
        // that's annoying exactly once, in the incident where you need it.
        let online: boolean | undefined;
        try { online = typeof navigator !== "undefined" ? navigator.onLine : undefined; } catch { /* ignore */ }
        const ctx = describePayload({ ...context, online });
        err.stack = `capgo payload: ${describePayload(payload)}\ncapgo context: ${ctx}\n${err.stack ?? ""}`;
        return err;
      };

      await updater.addListener("updateFailed", (payload) => {
        const v = payload?.bundle?.version ?? payload?.version ?? "unknown";
        reportError(
          withPayload(
            new Error(`[capgo] update failed — bundle ${v} did not boot; rolled back to previous`),
            payload,
          ),
          "capgo-update-failed",
        );
      }).catch(() => { /* event unsupported on this plugin version — no-op */ });
      await (updater.addListener as unknown as (e: string, l: (p: { status?: string; bundle?: { version?: string } }) => void) => Promise<unknown>)(
        "appReady",
        (payload) => {
          if (payload?.status) context.lastStatus = String(payload.status).slice(0, 200);
          if (payload?.bundle?.version) context.running = payload.bundle.version;
        },
      ).catch(() => { /* event unsupported — no-op */ });
      await updater.addListener("downloadFailed", async (payload) => {
        const v = payload?.version ?? payload?.bundle?.version ?? "unknown";
        // A "download failure" for the version this bundle IS is not a
        // download at all: the plugin aborted on a stale leftover copy of the
        // same version (the loser of an earlier double download, left in an
        // error state) and reports the abort through this event. The phone is
        // fine — it is running the version. Clean the leftover below, but
        // never count or alert (Sentry 2026-09-16: "v=1.3.63 … failed
        // (version 1.3.63, attempt 2)" emailed the founder about a healthy phone).
        let runningVersion: string | undefined;
        try { runningVersion = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : undefined; } catch { runningVersion = undefined; }
        const isRunningVersion = String(v) === runningVersion || String(v) === context.running;
        if (isRunningVersion) {
          context.lastStatus = `abort for the running version ${v} (stale copy)`;
          // eslint-disable-next-line no-console
          console.warn(`[live-update] plugin reported a download failure for the running version ${v}; cleaning the stale copy, not an error`);
        }
        // One failed download is normal life on a phone: the user force-quit
        // while it was still downloading, or lost signal. The plugin retries
        // on the next launch. Only a REPEAT failure for the same version on
        // this device is worth an alert — that is a phone that is stuck.
        // (Founder got an email per single failure; review 2026-09-14.)
        // Repeats within two minutes are ONE attempt: the plugin's abort and
        // our manual download both fire this for a single failed foreground
        // (Sentry 2026-09-16 — "attempt 2" and "attempt 3" one second apart).
        let attempt = 1;
        let counted = true;
        if (!isRunningVersion) {
          try {
            const { countDownloadFailure } = await import("./capgoBundleWait");
            ({ attempt, counted } = countDownloadFailure(localStorage, String(v)));
          } catch { /* storage or import unavailable — treat as first */ }
          context.lastStatus = `download failed (attempt ${attempt})`;
        }
        if (isRunningVersion) {
          /* handled above — fall through to the cleanup only */
        } else if (!counted) {
          // eslint-disable-next-line no-console
          console.warn(`[live-update] bundle ${v} download failed again within the same window (attempt ${attempt}); not re-counted`);
        } else if (attempt < 2) {
          // eslint-disable-next-line no-console
          console.warn(`[live-update] bundle ${v} download failed (attempt ${attempt}); will retry next launch`);
        } else {
          reportError(
            withPayload(new Error(`[capgo] bundle download failed (version ${v}, attempt ${attempt})`), payload),
            "capgo-download-failed",
          );
        }
        // Self-heal (2026-09-10): if that version already sits on the phone in
        // an error state, the plugin will abort every future check with this
        // same event until a newer release exists. Delete the errored copy so
        // the next foreground check downloads it again. Never the running one.
        void (async () => {
          try {
            const { findErroredBundle } = await import("./capgoBundleWait");
            const loose = CapacitorUpdater as unknown as {
              list: () => Promise<{ bundles?: { id?: string; version?: string; status?: string }[] }>;
              current: () => Promise<{ bundle?: { id?: string } }>;
              delete: (o: { id: string }) => Promise<void>;
            };
            const [listed, cur] = await Promise.all([loose.list(), loose.current().catch(() => undefined)]);
            const bad = findErroredBundle(listed?.bundles, String(v));
            if (bad?.id && bad.id !== cur?.bundle?.id) {
              await loose.delete({ id: bad.id });
              // eslint-disable-next-line no-console
              console.warn(`[live-update] removed errored bundle ${bad.id} (${v}) so the next check can retry`);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[live-update] errored-bundle cleanup failed", e);
          }
        })();
      }).catch(() => { /* event unsupported on this plugin version — no-op */ });

      await updater.addListener("downloadComplete", (payload) => {
        try {
          const dv = payload?.bundle?.version ?? payload?.version;
          if (dv) {
            localStorage.removeItem(`trybeup_capgo_dl_fail:${dv}`);
            localStorage.removeItem(`trybeup_capgo_dl_fail_at:${dv}`);
          }
        } catch { /* ignore */ }
        const id = payload?.bundle?.id;
        if (!id) return;
        // Stage the new bundle but DO NOT apply now. The previous
        // version applied immediately on downloadComplete to shave one
        // bg→fg cycle off the rollout, but that fired the CapacitorUpdater
        // .set() WebView teardown while the user could be mid-tap —
        // producing the intermittent "blank screen / header stays, body
        // gone" the founder reported (2026-06-22). Capgo eventually
        // settled the new bundle, which is why a force-quit + reopen
        // always "fixed" it — that was just the rollout completing.
        //
        // The genuine-foreground listener below is the only safe place to
        // apply: bg→fg means the user has just returned, no input is being
        // interrupted, and a brief reload is invisible under the system
        // foreground transition.
        pending = { id, version: payload.bundle?.version };

        // First-tap refinement (2026-07-27): if the user has ALREADY
        // tapped "Update" and we were just waiting on this download, apply
        // it now instead of making them tap a second time. Guarded by the
        // workout check and the 2-minute intent window; applyInFlight (and
        // applyPendingCapgoUpdate's own re-entrancy latch) prevent this
        // from racing an in-progress apply.
        if (
          applyRequestedAt &&
          Date.now() - applyRequestedAt < APPLY_INTENT_MAX_AGE_MS &&
          !applyInFlight &&
          !isWorkoutInProgress()
        ) {
          applyRequestedAt = 0;
          applyPendingCapgoUpdate(payload.bundle?.version).catch(() => { /* logged inside */ });
        }
      });

      // Foreground transition. Apply any pending bundle so the user
      // returns to the new version without doing anything. We also
      // honour the close-and-reopen path: if Capgo has the bundle
      // queued but didn't fire downloadComplete during this session,
      // a future cold start still picks it up.
      //
      // `resume` after a `pause`, NOT appStateChange isActive: iOS fires
      // isActive:true when a system sheet closes (Google sign-in, the
      // StoreKit purchase sheet, a permission prompt), and applying there
      // reloaded the app mid sign-in / mid purchase (review 2026-10-17).
      void onGenuineForeground(
        () => {
          void syncWorkoutUpdateDelay();
          if (!pending) return;
          // NEVER swap bundles under a live workout — backgrounding mid-set
          // (checking a text between sets) is the normal gym case, and the
          // reload nuked the session (founder incident 2026-07-03). The
          // update waits for a workout-free foregrounding or cold start.
          if (isWorkoutInProgress()) return;
          // Fire and forget — the resulting reload will tear down the
          // current JS context anyway. .catch() for the same reason as
          // the downloadComplete listener above.
          applyPendingCapgoUpdate().catch(() => { /* logged inside */ });
        },
        // Last chance before the plugin's own background install. Usually
        // the interval below got there first.
        () => { void syncWorkoutUpdateDelay(); },
      );

      // The native workout delay: now, and every few seconds while open.
      void syncWorkoutUpdateDelay();
      window.setInterval(() => {
        if (typeof document === "undefined" || document.visibilityState === "visible") {
          void syncWorkoutUpdateDelay();
        }
      }, WORKOUT_DELAY_POLL_MS);
    } catch {
      /* Capgo plugin missing (e.g. web preview build) — degrade silently. */
    }
  })();
}
